import React, { memo, useMemo } from 'react';
import styled from 'styled-components';

import { StudentColors } from './studentColors';

interface IIdentityAnchorProps {
  /** Student full name; drives the initials and the accessible label. */
  fullName: string;
}

/** Derive up to two uppercase initials from a full name. */
const initialsOf = (name: string): string => {
  const parts = name
    .split(/\s+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return '';
  const letters = parts.slice(0, 2).map((p) => p[0].toUpperCase());
  return letters.join('');
};

/**
 * Identity visual anchor for the Student card (Area 4, Req 13).
 *
 * Renders initials derived from the student's full name; when the name is
 * unavailable shows a neutral placeholder glyph regardless of any other source
 * (Req 13.2). Colors come from `StudentColors` tokens (Req 13.3). The student's
 * name is exposed as the accessible label.
 */
export const IdentityAnchor = memo(({ fullName }: IIdentityAnchorProps) => {
  const initials = useMemo(() => initialsOf(fullName), [fullName]);
  const hasName = initials.length > 0 && fullName.trim() !== '—';

  return (
    <Anchor
      isPlaceholder={!hasName}
      aria-label={hasName ? fullName : undefined}
      title={hasName ? fullName : undefined}
    >
      {hasName ? initials : '•'}
    </Anchor>
  );
});

const Anchor = styled.div<{ isPlaceholder: boolean }>`
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: ${StudentColors.surface};
  background: ${({ isPlaceholder }) =>
    isPlaceholder ? StudentColors.neutral : StudentColors.accent};
  user-select: none;
`;
